import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dropdown",
    "search",
    "frame",
    "loading",
    "resource",
  ]

  connect() {
    this.resizeObserver = new ResizeObserver(() => {
      this.debounceUpdateLayout()
    })
    this.mutationObserver = new MutationObserver(() => this.updateLayout())

    this.resizeObserver.observe(this.dropdownTarget)

    this.frameTargets.forEach((frame) => {
      this.mutationObserver.observe(frame, { childList: true, subtree: true })
      this.showLoading(frame)
    })

    requestAnimationFrame(() => this.updateLayout())
  }

  disconnect() {
    this.resizeObserver.disconnect()
    this.mutationObserver.disconnect()
    clearTimeout(this.searchTimeout)
    cancelAnimationFrame(this.layoutFrame)
  }

  debounceUpdateLayout() {
    cancelAnimationFrame(this.layoutFrame)
    this.layoutFrame = requestAnimationFrame(() => this.updateLayout())
  }

  updateLayout() {
    this.dropdownTarget.style.height = ""

    const style = window.getComputedStyle(this.dropdownTarget)
    const maxDropdownHeight = parseFloat(style.maxHeight)

    if (!maxDropdownHeight) {
      return
    }

    const contentHeight = this.dropdownTarget.scrollHeight
    const frameContentHeight = this.frameTargets.reduce((sum, frame) => {
      return sum + frame.scrollHeight
    }, 0)
    const nonFrameHeight = contentHeight - frameContentHeight

    const layoutClasses = ["flex-none", "flex-1", "flex-auto", "min-h-0"]

    if (contentHeight > maxDropdownHeight) {
      this.dropdownTarget.style.height = `${maxDropdownHeight}px`

      const visibleFrames = this.frameTargets.filter((frame) => frame.scrollHeight)
      const availableFrameHeight = maxDropdownHeight - nonFrameHeight
      const maxFrameHeight = availableFrameHeight / visibleFrames.length

      this.frameTargets.forEach((frame) => {
        frame.classList.remove(...layoutClasses)

        if (frame.scrollHeight < maxFrameHeight) {
          frame.classList.add("flex-none")
        } else {
          frame.classList.add("flex-1", "min-h-0")
        }
      })
    } else {
      this.dropdownTarget.style.height = "auto"

      this.frameTargets.forEach((frame) => {
        frame.classList.remove(...layoutClasses)

        frame.classList.add("flex-none")
      })
    }
  }

  focus() {
    this.searchTarget.focus()
  }

  resourceTargetConnected(element) {
    if ("dashboardSelectorCurrentResource" in element.dataset) {
      if ("dashboardSelectorGrouped" in element.dataset) {
        const container = element.parentElement
        const heading = container.previousElementSibling

        container.parentElement.prepend(heading, container)

        return
      }

      element.parentElement.prepend(element)
    }
  }

  search(event) {
    const search = event.target.value

    // show loading state immediately
    this.frameTargets.forEach((frame) => this.showLoading(frame))

    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
      this.frameTargets.forEach((frame) => {
        const url = new URL(frame.src, document.location)
        const params = url.searchParams

        params.set("search", search)
        url.search = params.toString()

        frame.src = url.toString()
      })
    }, 200)
  }

  clear() {
    this.searchTarget.value = ""

    this.frameTargets.forEach((frame) => {
      const url = new URL(frame.src, document.location)
      url.search = ""

      this.showLoading(frame)

      frame.src = url.toString()
    })
  }

  showLoading(frame) {
    frame.innerHTML = this.loadingTarget.innerHTML
  }
}
